import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isNil, Nillable } from '@web/utils';
import { interval, startWith } from 'rxjs';
import { BoxQuestionnaireStatus } from '../../enums';

@Component({
  selector: 'app-box-questionnaire-availability',
  imports: [DatePipe],
  templateUrl: './box-questionnaire-availability.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoxQuestionnaireAvailabilityComponent {
  private readonly destroyRef = inject(DestroyRef);

  public readonly createdAt = input<Nillable<Date>>(null);
  public readonly endDate = input.required<Date>();
  public readonly isEnding = input(false);
  public readonly status = input<Nillable<BoxQuestionnaireStatus>>(null);
  public readonly withLabel = input<boolean>(true);

  protected readonly currentTime = signal(new Date());
  protected readonly withProgressBar = computed(() => !isNil(this.progress()));

  protected readonly progress = computed<Nillable<number>>(() => {
    const createdAt = this.createdAt();

    if (isNil(createdAt) || this.status() === BoxQuestionnaireStatus.Ended) {
      return null;
    }

    const totalDuration = this.endDate().getTime() - createdAt.getTime();

    if (totalDuration <= 0) {
      return 100;
    }

    const elapsedDuration = this.currentTime().getTime() - createdAt.getTime();

    return Math.min(Math.max((elapsedDuration / totalDuration) * 100, 0), 100);
  });

  public constructor() {
    this.initializeCurrentTimeSubscription();
  }

  private initializeCurrentTimeSubscription(): void {
    interval(1000)
      .pipe(startWith(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.currentTime.set(new Date()));
  }
}
